<template>
  <div class="container-fluid">
    <div class="page-wrapper">
      <div slot="header" class="page-wrapper-header">
        <div class="row">
          <div class="col-6">
            <h3 class="mb-0">
              {{ $t("THIRD_PARTY_SERVICES.THIRD_PARTY_SERVICES_LIST") }}
            </h3>
          </div>
          <div class="col-6 text-right">
            <base-button
              class="elite-button add"
              icon
              size="sm"
              @click="openThirdPartyServiceCreateModal"
              v-if="
                $currentUserCan($permissions.PERM_CREATE_THIRD_PARTY_SERVICES)
              "
            >
              <span class="btn-inner--icon">
                <i class="far fa-plus-circle"></i>
              </span>
              <span class="btn-inner--text">
                {{ $t("THIRD_PARTY_SERVICES.ADD_THIRD_PARTY_SERVICE") }}
              </span>
            </base-button>
            <notification-subscription
              v-if="
                $currentUserCan($permissions.PERM_VIEW_THIRD_PARTY_SERVICES)
              "
              :objectType="'third-party-services'"
              :events="{
                CREATE: $t('NOTIFICATIONS.EVENT_CREATE'),
                UPDATE: $t('NOTIFICATIONS.EVENT_UPDATE'),
                DELETE: $t('NOTIFICATIONS.EVENT_DELETE'),
              }"
            />
          </div>
        </div>
      </div>

      <third-party-service-list-table
        @onViewThirdPartyService="openThirdPartyServiceViewModal"
        @onEditThirdPartyService="openThirdPartyServiceEditModal"
        @onDeleteThirdPartyService="deleteThirdPartyService"
        :key="renderKey * 100"
      />

      <div
        v-if="isViewThirdPartyServiceModalOpened"
        class="resizable-wrapper"
        v-bind:class="[isViewThirdPartyServiceModalOpened ? 'show' : 'hide']"
        :key="renderKey * 200"
      >
        <div
          class="resizable-wrapper-content"
          v-resizable-modal="'THIRD_PARTY_SERVICE_VIEW'"
        >
          <div class="resizable-wrapper-content-header">
            <div class="resizable-wrapper-content-header-left">
              <h1>
                {{ $t("THIRD_PARTY_SERVICES.VIEW_THIRD_PARTY_SERVICE") }}
              </h1>
            </div>
            <div class="resizable-wrapper-content-header-right">
              <notification-subscription
                v-if="openThirdPartyService"
                :objectType="'third-party-services'"
                :objectId="openThirdPartyService.id"
                :events="{
                  UPDATE: $t('NOTIFICATIONS.EVENT_UPDATE'),
                  DELETE: $t('NOTIFICATIONS.EVENT_DELETE'),
                }"
              />
              <base-dropdown
                title-classes="btn dropdown-button"
                menu-on-right
                :has-toggle="false"
              >
                <template slot="title">
                  <i class="fas fa-ellipsis-v"></i>
                </template>
                <button
                  class="edit"
                  @click="openThirdPartyServiceEditModal(openThirdPartyService)"
                >
                  <i class="fal fa-edit"></i>
                  <span>{{ $t("COMMON.EDIT") }}</span>
                </button>
                <button
                  class="delete"
                  @click="deleteThirdPartyService(openThirdPartyService)"
                >
                  <i class="fal fa-trash-alt"></i>
                  <span>{{ $t("COMMON.DELETE") }}</span>
                </button>
              </base-dropdown>
              <button class="close" @click="closeThirdPartyServiceModal">
                <i class="fal fa-times"></i>
              </button>
            </div>
          </div>
          <div class="resizable-wrapper-content-body">
            <view-third-party-service-component
              v-if="openThirdPartyService"
              :thirdPartyServiceId="openThirdPartyService.id"
            />
          </div>
        </div>
      </div>

      <div
        v-if="isEditThirdPartyServiceModalOpened"
        class="resizable-wrapper"
        v-bind:class="[isEditThirdPartyServiceModalOpened ? 'show' : 'hide']"
        :key="renderKey * 300"
      >
        <div
          class="resizable-wrapper-content"
          v-resizable-modal="'THIRD_PARTY_SERVICE'"
        >
          <div class="resizable-wrapper-content-header">
            <div class="resizable-wrapper-content-header-left">
              <h1>
                {{ $t("THIRD_PARTY_SERVICES.EDIT_THIRD_PARTY_SERVICE") }}
              </h1>
            </div>
            <div class="resizable-wrapper-content-header-right">
              <base-dropdown
                title-classes="btn dropdown-button"
                menu-on-right
                :has-toggle="false"
              >
                <template slot="title">
                  <i class="fas fa-ellipsis-v"></i>
                </template>
                <button
                  class="view"
                  @click="openThirdPartyServiceViewModal(openThirdPartyService)"
                >
                  <i class="fal fa-expand-alt"></i>
                  <span>{{ $t("COMMON.VIEW") }}</span>
                </button>
              </base-dropdown>
              <button class="close" @click="closeThirdPartyServiceModal">
                <i class="fal fa-times"></i>
              </button>
            </div>
          </div>
          <div class="resizable-wrapper-content-body">
            <edit-third-party-service-component
              v-if="openThirdPartyService"
              :thirdPartyServiceId="openThirdPartyService.id"
              @onViewThirdPartyService="openThirdPartyServiceViewModal"
            />
          </div>
        </div>
      </div>

      <div
        v-if="isAddThirdPartyServiceModalOpened"
        class="resizable-wrapper"
        v-bind:class="[isAddThirdPartyServiceModalOpened ? 'show' : 'hide']"
        :key="renderKey * 400"
      >
        <div
          class="resizable-wrapper-content"
          v-resizable-modal="'THIRD_PARTY_SERVICE'"
        >
          <div class="resizable-wrapper-content-header">
            <div class="resizable-wrapper-content-header-left">
              <h1>
                {{ $t("THIRD_PARTY_SERVICES.ADD_THIRD_PARTY_SERVICE") }}
              </h1>
            </div>
            <div class="resizable-wrapper-content-header-right">
              <button class="close" @click="closeThirdPartyServiceModal">
                <i class="fal fa-times"></i>
              </button>
            </div>
          </div>
          <div class="resizable-wrapper-content-body">
            <add-third-party-service-component
              @onViewThirdPartyService="openThirdPartyServiceViewModal"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import swal from "sweetalert2";
import { Button } from "element-ui";
import requestErrorMixin from "@/mixins/request-error-mixin";
import {
  QUERY_ACTIONS_VIEW,
  QUERY_ACTIONS_EDIT,
  QUERY_ACTIONS_ADD,
} from "@/constants/common";
import NotificationSubscription from "@/components/NotificationSubscription.vue";
import ThirdPartyServiceListTable from "./partials/ThirdPartyServiceListTable.vue";
import EditThirdPartyServiceComponent from "./components/EditThirdPartyServiceComponent.vue";
import AddThirdPartyServiceComponent from "./components/AddThirdPartyServiceComponent.vue";
import ViewThirdPartyServiceComponent from "./components/ViewThirdPartyServiceComponent.vue";

export default {
  layout: "DashboardLayout",

  components: {
    ThirdPartyServiceListTable,
    NotificationSubscription,
    EditThirdPartyServiceComponent,
    AddThirdPartyServiceComponent,
    ViewThirdPartyServiceComponent,
    [Button.name]: Button,
  },

  mixins: [requestErrorMixin],

  computed: {},

  watch: {},

  data() {
    const thirdPartyServiceId = this.$route.query.id;
    const action = this.$route.query.action;
    let isViewThirdPartyServiceModalOpened = false;
    let isEditThirdPartyServiceModalOpened = false;
    let isAddThirdPartyServiceModalOpened = false;
    let openThirdPartyService = null;
    if (thirdPartyServiceId && action) {
      if (action === QUERY_ACTIONS_VIEW) {
        isViewThirdPartyServiceModalOpened = true;
      } else if (action === QUERY_ACTIONS_EDIT) {
        isEditThirdPartyServiceModalOpened = true;
      }
      openThirdPartyService = { id: thirdPartyServiceId };
    } else if (action === QUERY_ACTIONS_ADD) {
      isAddThirdPartyServiceModalOpened = true;
    }
    return {
      isViewThirdPartyServiceModalOpened: isViewThirdPartyServiceModalOpened,
      isEditThirdPartyServiceModalOpened: isEditThirdPartyServiceModalOpened,
      isAddThirdPartyServiceModalOpened: isAddThirdPartyServiceModalOpened,
      openThirdPartyService: openThirdPartyService,
      renderKey: 1,
    };
  },

  methods: {
    openThirdPartyServiceCreateModal() {
      this.closeThirdPartyServiceModal();
      this.isAddThirdPartyServiceModalOpened = true;

      history.pushState(
        {},
        null,
        this.$router.resolve({
          name: "List Third Party Services",
          query: { action: QUERY_ACTIONS_ADD },
        }).href
      );
    },
    openThirdPartyServiceViewModal(thirdPartyServices, reRender = false) {
      this.closeThirdPartyServiceModal();
      this.openThirdPartyService = thirdPartyServices;
      this.isViewThirdPartyServiceModalOpened = true;
      if (reRender) {
        this.renderKey++;
      }

      history.pushState(
        {},
        null,
        this.$router.resolve({
          name: "List Third Party Services",
          query: {
            id: this.openThirdPartyService.id,
            action: QUERY_ACTIONS_VIEW,
          },
        }).href
      );
    },
    openThirdPartyServiceEditModal(thirdPartyServices) {
      this.closeThirdPartyServiceModal();
      this.openThirdPartyService = thirdPartyServices;
      this.isEditThirdPartyServiceModalOpened = true;

      history.pushState(
        {},
        null,
        this.$router.resolve({
          name: "List Third Party Services",
          query: {
            id: this.openThirdPartyService.id,
            action: QUERY_ACTIONS_EDIT,
          },
        }).href
      );
    },
    closeThirdPartyServiceModal() {
      this.isAddThirdPartyServiceModalOpened = false;
      this.isViewThirdPartyServiceModalOpened = false;
      this.isEditThirdPartyServiceModalOpened = false;
      this.openThirdPartyService = null;

      history.pushState(
        {},
        null,
        this.$router.resolve({
          name: "List Third Party Services",
          query: {},
        }).href
      );
    },

    async deleteThirdPartyService(thirdPartyServices) {
      const confirmation = await swal.fire({
        text: this.$t("THIRD_PARTY_SERVICES.DELETE_THIS_THIRD_PARTY_SERVICE"),
        type: "question",
        customClass: {
          popup: "delete-popup",
        },
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      try {
        if (confirmation.value === true) {
          await this.$store.dispatch(
            "thirdPartyServices/destroy",
            thirdPartyServices.id
          );
          this.renderKey++;
          this.closeThirdPartyServiceModal();
          this.$notify({
            type: "success",
            message: this.$t(
              "THIRD_PARTY_SERVICES.THIRD_PARTY_SERVICE_DELETED"
            ),
          });
        }
      } catch (error) {
        await this.showRequestError(error);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },
  },
};
</script>
